import React from 'react'
import { Clock, Globe } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTimezone } from '@/hooks/useTimezone'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Badge } from '@/components/ui/badge'

interface TimezoneIndicatorProps {
  /** Size variant for the indicator */
  size?: 'sm' | 'md' | 'lg'
  /** Style variant for the indicator */
  variant?: 'badge' | 'text' | 'icon' | 'full'
  /** Whether to show full timezone name or just abbreviation */
  showFullName?: boolean
  /** Whether to show UTC offset */
  showOffset?: boolean
  /** Custom className */
  className?: string
  /** Whether to show tooltip with more timezone information */
  showTooltip?: boolean
  /** Custom icon to use instead of default */
  icon?: React.ReactNode
  /** Additional label text */
  label?: string
}

export function TimezoneIndicator({ 
  size = 'sm',
  variant = 'text',
  showFullName = false,
  showOffset = false,
  className,
  showTooltip = true,
  icon,
  label = 'Time shown in'
}: TimezoneIndicatorProps) {
  const { 
    timezone, 
    getAbbreviation, 
    getDisplayName, 
    isLoading,
  } = useTimezone()

  if (isLoading) {
    return (
      <div className={cn('text-muted-foreground animate-pulse', className)}>
        <div className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          <span className="text-xs">Loading timezone...</span>
        </div>
      </div>
    )
  }

  const abbreviation = getAbbreviation()
  const displayName = getDisplayName()
  
  // Get timezone offset information
  const getTimezoneOffset = (timezone: string) => {
    try {
      const now = new Date()
      const utc = new Date(now.getTime() + (now.getTimezoneOffset() * 60000))
      const local = new Date(utc.toLocaleString('en-US', { timeZone: timezone }))
      const offset = (local.getTime() - utc.getTime()) / (1000 * 60 * 60)
      const sign = offset >= 0 ? '+' : '-'
      const absOffset = Math.abs(offset)
      const hours = Math.floor(absOffset)
      const minutes = (absOffset % 1) * 60
      return `${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
    } catch (error) {
      return '+00:00'
    }
  }
  
  const offsetString = getTimezoneOffset(timezone)
  const isDSTActive = new Date().toLocaleDateString('en', { timeZone: timezone, timeZoneName: 'short' }).includes('DT')

  // Content to display based on variant
  const getContent = () => {
    switch (variant) {
      case 'badge':
        return (
          <Badge variant="outline" className={cn('text-xs', className)}>
            {icon || <Clock className="h-3 w-3 mr-1" />}
            {showFullName ? displayName : abbreviation}
            {showOffset && ` (${offsetString})`}
          </Badge>
        )
      
      case 'icon':
        return (
          <div className={cn('flex items-center gap-1 text-muted-foreground', className)}>
            {icon || <Clock className="h-4 w-4" />}
          </div>
        )
      
      case 'full':
        return (
          <div className={cn('flex items-center gap-2 text-sm text-muted-foreground', className)}>
            {icon || <Globe className="h-4 w-4" />}
            <span>
              {label} {showFullName ? displayName : timezone} 
              {showOffset && ` (${offsetString})`}
            </span>
          </div>
        )
      
      case 'text':
      default:
        const sizeClasses = {
          sm: 'text-xs',
          md: 'text-sm', 
          lg: 'text-base'
        }
        
        return (
          <span className={cn(
            'text-muted-foreground flex items-center gap-1',
            sizeClasses[size],
            className
          )}>
            {icon || <Clock className="h-3 w-3" />}
            {showFullName ? displayName : abbreviation}
            {showOffset && ` (${offsetString})`}
          </span>
        )
    }
  }

  const content = getContent()

  if (!showTooltip) {
    return content
  }

  // Tooltip content with detailed timezone information
  const tooltipContent = (
    <div className="space-y-2 text-xs">
      <div className="font-medium">{displayName}</div>
      <div className="space-y-1 text-muted-foreground">
        <div>Timezone: {timezone}</div>
        <div>Abbreviation: {abbreviation}</div>
        <div>UTC Offset: {offsetString}</div>
        {isDSTActive && (
          <div className="text-orange-500">🌞 Daylight Saving Time is active</div>
        )}
      </div>
    </div>
  )

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help">
            {content}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          {tooltipContent}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export default TimezoneIndicator